import { useCallback, useEffect, useMemo } from 'react';
import useLocalStorageState from '../../../utils/useLocalStorageState';
import useGetDivisions from '../useGetDivisions';
import {
    countTotalNumFilters,
    FILTER_TYPES,
    getDefaultValues,
    isAppliedFilterValue,
    renderAppliedFiltersDescription,
} from '../../../utils/filterUtils';
import StatusPill from '../../Components/Invoicing/StatusPill';

const useInvoicingFilters = (storageKey = '') => {
    const { data: divisions, isLoading: isLoadingDivisions } = useGetDivisions();
    const divisionsNames = useMemo(
        () => divisions?.map((d) => d?.description)?.filter(Boolean) ?? [],
        [divisions]
    );
    const { state: filters, setValue: setFilters } = useLocalStorageState(`${storageKey}Filters`);
    const handleApplyFilter = (newFilters) => {
        const hasThroughDate = isAppliedFilterValue(newFilters?.invoiceThroughDate);
        const hasTransactionRange = isAppliedFilterValue(newFilters?.transactionDateRange);

        if (hasThroughDate && hasTransactionRange) {
            setFilters({
                ...newFilters,
                transactionDateRange: { from: null, to: null },
            });
            return;
        }

        setFilters(newFilters);
    };

    const filtersSetup = useMemo(() => {
        let defaultFilters = [
            {
                name: 'types',
                label: 'Types',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: {
                    options:
                        storageKey === 'invoiceManagement'
                            ? [
                                  { label: 'Timesheet', value: 'TimeOnly' },
                                  { label: 'Expense', value: 'ExpenseOnly' },
                                  { label: 'Timesheet & Expense', value: 'TimeAndExpense' },
                                  { label: 'Deposit', value: 'Deposits' },
                                  { label: 'Credit Memo', value: 'CreditMemos' },
                                  { label: 'Perm Fee', value: 'PermFees' },
                                  { label: 'Finance Fee', value: 'FinanceFees' },
                              ]
                            : [
                                  { label: 'Timesheet', value: 'Timesheet' },
                                  { label: 'Expense', value: 'Expense' },
                                  { label: 'Timesheet & Expense', value: 'TimeAndExpense' },
                              ],
                    emptyLabel: 'All',
                },
            },
        ];

        defaultFilters.push({
            name: 'transactionDateRange',
            label: 'Transaction Date Range',
            type: FILTER_TYPES.DATE_RANGE,
            defaultValue: { from: null, to: null },
            mutuallyExclusiveWith:
                storageKey === 'invoiceInitiation' ? ['invoiceThroughDate'] : undefined,
            tooltip:
                'Timesheets: Full timesheet returned if any entry matches the date range\n\nPartial corrections: Returns all corrected entries if any corrected date matches the date range\n\nExpenses: Only entries matching the date range are returned',
        });

        if (storageKey === 'invoiceInitiation') {
            defaultFilters.push({
                name: 'invoiceThroughDate',
                label: 'Invoice Through Date',
                type: FILTER_TYPES.DATE,
                defaultValue: null,
                mutuallyExclusiveWith: ['transactionDateRange'],
                tooltip:
                    'Returns processed timesheet and expense line items with applicable dates on or before the selected date.',
            });
        }

        if (storageKey === 'invoiceManagement') {
            defaultFilters = defaultFilters.concat([
                {
                    name: 'invoiceDate',
                    label: 'Invoice Date',
                    type: FILTER_TYPES.DATE_RANGE,
                    defaultValue: { from: null, to: null },
                },
                {
                    name: 'postingDate',
                    label: 'Posting Date',
                    type: FILTER_TYPES.DATE_RANGE,
                    defaultValue: { from: null, to: null },
                },
            ]);
        }

        defaultFilters.push({
            name: 'invoiceFrequency',
            label: 'Frequency',
            type: FILTER_TYPES.CHECKBOX,
            defaultValue: [],
            overrides: {
                options: [
                    { label: 'Weekly', value: 'Weekly' },
                    { label: 'Monthly', value: 'Monthly' },
                    {
                        label: 'Bi-Weekly',
                        value: storageKey === 'invoiceManagement' ? 'BiWeekly' : 'Bi-Weekly',
                    },
                    {
                        label: '15 Days After Month-End',
                        value:
                            storageKey === 'invoiceManagement'
                                ? 'FifteenDaysFollowingTheEndOfTheMonth'
                                : '15 Days Following the end of the month',
                    },
                ],
                emptyLabel: 'All',
            },
        });

        if (storageKey === 'invoiceManagement') {
            defaultFilters.push({
                name: 'invoiceStatus',
                label: 'Statuses',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                isQuickFilter: true,
                overrides: {
                    options: [],
                    emptyLabel: 'All',
                    render: (value) => <StatusPill status={value} />,
                },
            });
        }

        defaultFilters.push(
            {
                name: 'divisions',
                label: 'Divisions',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: { options: divisionsNames, emptyLabel: 'All' },
            },
            {
                name: 'bookingId',
                label: 'Booking Number Range',
                type: FILTER_TYPES.NUMERIC_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'Flags',
                label: 'Flags',
                type: FILTER_TYPES.FLAG,
                defaultValue: [],
                isQuickFilter: true,
                overrides: {
                    options: [
                        {
                            label: 'Correction',
                            value: 'CorrectionsOnly',
                            exclude: ['ExcludeCorrections'], // Can't be used when ExcludeCorrections is selected
                        },
                        {
                            label: 'Exclude Corrections',
                            value: 'ExcludeCorrections',
                            exclude: ['CorrectionsOnly'], // Can't be used when CorrectionOnly is selected
                        },
                        { label: 'High Priority', value: 'IsHighPriority' },
                    ],
                    emptyLabel: 'All',
                },
            },
            {
                name: 'weekEndingDate',
                label: 'Week Ending',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            }
        );

        return defaultFilters;
    }, [divisionsNames, storageKey]);

    useEffect(() => {
        if (filters) return;
        // If filters are not set, set them to the default values
        const defaultFiltersValue = {};
        filtersSetup.forEach(({ name, defaultValue }) => {
            if (isAppliedFilterValue(defaultValue)) {
                defaultFiltersValue[name] = defaultValue;
            }
        });
        if (Object.keys(defaultFiltersValue).length > 0) {
            setFilters(defaultFiltersValue);
        }
    }, [filtersSetup]);

    const appliedFiltersDescription = useMemo(
        () => renderAppliedFiltersDescription(filters, filtersSetup),
        [filters, filtersSetup]
    );

    const resetFilters = useCallback(() => {
        setFilters(getDefaultValues(filtersSetup));
    }, [filtersSetup]);

    const hasAppliedFilters = useMemo(() => countTotalNumFilters(filters) > 0, [filters]);

    if (!storageKey) {
        throw new Error(
            'Storage key is required and should be unique to prevent overwriting data.'
        );
    }

    return {
        filters,
        hasAppliedFilters,
        setFilters,
        resetFilters,
        filtersSetup,
        handleApplyFilter,
        isLoadingFilterConfig: isLoadingDivisions,
        appliedFiltersDescription,
    };
};

export default useInvoicingFilters;
